<script setup lang="ts">
import {
  iSwitch,
  iTextInput,
  iTextInputModes,
  iButton,
  iButtonTypes,
  iButtonSizes,
  iButtonStyles,
  iSpinner,
  showToast,
} from '@inscribe/signal';
import type { ConfigurableInsight } from '@/types/decision-engine/configurable-insights.types';
import { ref, computed } from 'vue';
import { useConfigurableInsightsStore } from '@/V2/stores/pinia/decision-engine/configurable-insights.store';
import { validateInsightConditions } from '@/utils/decision-engine/configurable-insights';

const store = useConfigurableInsightsStore();

const nameErrorMessage = ref('');
const showNameHint = ref(false);
const isGenerating = ref(false);

const props = defineProps<{
  insight: ConfigurableInsight;
}>();

const hasConditions = computed(
  () => props.insight.configuration.conditions.length > 0,
);

function validateName(name: string | undefined) {
  if (name === '' || name === undefined) {
    nameErrorMessage.value = 'Insight name is required';
    return false;
  } else {
    nameErrorMessage.value = '';
    return true;
  }
}

async function onGenerateClick() {
  const { isValid, errors } = validateInsightConditions(props.insight);
  if (!isValid) {
    showToast({ text: errors.join('\n'), isError: true });
    return;
  }

  isGenerating.value = true;
  try {
    const result = await store.generateNameAndDescription(props.insight);
    if (result?.signal_name) props.insight.signal_name = result.signal_name;
    if (result?.description)
      props.insight.configuration.description = result.description;
  } catch {
    showToast({
      text: 'Failed to generate name and description',
      isError: true,
    });
  } finally {
    isGenerating.value = false;
  }
}

// Expose method to parent for validation on save attempt
defineExpose({
  attemptSave: () => {
    const isValid = validateName(props.insight.signal_name);
    showNameHint.value = !isValid;
  },
});
</script>

<template>
  <div class="settings-section d-flex flex-column i-gap-8">
    <div class="d-flex align-items-center justify-content-between">
      <h3 class="i-text-heading-6 title">Settings</h3>
      <i-button
        text="Generate Name and Description"
        :type="iButtonTypes.NEUTRAL"
        :size="iButtonSizes.SMALL"
        :button-style="iButtonStyles.SOLID_LIGHT"
        :disabled="isGenerating || !hasConditions"
        data-testid="generate-name-description-button"
        data-dd-action-name="generate-name-description-button"
        @button-click="onGenerateClick"
      />
    </div>

    <div class="form-container d-flex flex-column i-radius-5xl">
      <!-- Insight Name -->
      <div
        class="input-container d-flex flex-row align-items-start justify-content-between i-p-11"
      >
        <div class="description d-flex flex-column i-gap-1">
          <div class="label i-text-button-2">Name</div>
          <div class="description i-text-body-2">
            Display name for this insight
          </div>
        </div>
        <div class="input-wrapper d-flex justify-content-end">
          <i-spinner v-if="isGenerating" class="field-spinner" />
          <i-text-input
            v-model="insight.signal_name"
            :hint-text="nameErrorMessage"
            :show-hint-text="showNameHint"
            :has-error="showNameHint"
            data-testid="insight-name-input"
            data-dd-action-name="insight-name-input"
            placeholder="e.g. High Value Deposits"
          />
        </div>
      </div>

      <!-- Insight Description -->
      <div
        class="input-container d-flex flex-row align-items-start justify-content-between i-p-11"
      >
        <div class="description d-flex flex-column i-gap-1">
          <div class="label i-text-button-2">Description</div>
          <div class="description i-text-body-2">What this insight detects</div>
        </div>
        <div class="input-wrapper d-flex justify-content-end">
          <i-spinner v-if="isGenerating" class="field-spinner" />
          <i-text-input
            v-model="insight.configuration.description"
            :mode="iTextInputModes.TEXTAREA"
            data-testid="insight-description-input"
            data-dd-action-name="insight-description-input"
            placeholder="e.g. Flag if customer has more than 5 gambling transactions"
            :rows="3"
          />
        </div>
      </div>

      <!-- Impacts Risk Rating -->
      <div
        class="input-container d-flex flex-row align-items-center justify-content-between i-p-11"
      >
        <div class="description d-flex flex-column i-gap-1">
          <div class="label i-text-button-2">Impacts Risk Rating</div>
          <div class="description i-text-body-2">
            Whether this insight affects the customer risk rating
          </div>
        </div>
        <div class="input-wrapper d-flex justify-content-end">
          <i-switch
            v-model="insight.configuration.impacts_risk"
            data-testid="insight-impacts-risk-toggle"
            data-dd-action-name="insight-impacts-risk-toggle"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.settings-section {
  .title {
    color: var(--text-neutral-bold);
  }

  .form-container {
    background: var(--base);
    border: 1px solid var(--border-neutral-subtle);

    .input-container {
      &:not(:last-child) {
        border-bottom: 1px solid var(--border-neutral-subtle);
      }

      .input-wrapper {
        position: relative;
        width: 50%;

        .i-text-input {
          width: 100%;
        }

        .field-spinner {
          position: absolute;
          left: -28px;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
        }
      }

      .description {
        .label {
          color: var(--text-neutral-bold);
        }

        .description {
          color: var(--text-neutral);
        }
      }
    }
  }
}
</style>
